import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { forceCleanLogout, isAuthDeadError } from '@/lib/authRecovery';

// ============================================
// CONTEXTO CENTRALIZADO DE PERFIL DO USUÁRIO
// Fonte ÚNICA de dados de perfil em toda app
// ============================================

export interface UserProfile {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  equipe_id?: string | null;
  foto_url?: string | null;
  funcao?: string | null;
  roles?: string[];
}

export interface UserPermissions {
  ver_todos_clientes: boolean;
  ver_todos_pedidos: boolean;
}

interface UserProfileContextType {
  // Dados do perfil
  profile: UserProfile | null | undefined;
  permissions: UserPermissions | null | undefined;
  
  // Estados de loading
  isLoadingProfile: boolean;
  isLoadingPermissions: boolean;
  
  // Helpers
  isAuthenticated: boolean;
  userId: string | null;
  userRole: string | null;
  
  // Helpers de role
  isMaster: boolean;
  isGerente: boolean;
  isSupervisor: boolean;
  isBko: boolean;
  isQualidade: boolean;
  isConsultor: boolean;
  canSeeAll: boolean;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

// Constantes de roles com acesso total
const ROLES_ACESSO_TOTAL = ['master', 'gerente', 'diretor', 'bko', 'qualidade', 'coordenador'];

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // 🔄 Invalida cache quando auth muda (login/logout/token refresh)
  // Também detecta sessão morrendo no meio do uso e força logout limpo —
  // antes o front ficava preso 12h+ em loop tentando refresh quebrado.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[auth] state change:', event, 'has_session:', !!session);
        queryClient.invalidateQueries({ queryKey: ['auth-session'] });
        queryClient.invalidateQueries({ queryKey: ['user-profile'] });
        queryClient.invalidateQueries({ queryKey: ['user-permissions'] });

        // TOKEN_REFRESHED com session=null = refresh falhou definitivamente.
        // Diferente de SIGNED_OUT (que é logout manual ou expected).
        if (event === 'TOKEN_REFRESHED' && !session) {
          forceCleanLogout('refresh_failed');
        }
      }
    );
    return () => subscription.unsubscribe();
  }, [queryClient]);

  // 🚀 Query única de sessão - compartilhada em toda app.
  // Se getSession() retornar erro de refresh token, força logout limpo
  // (antes a UI travava em "Servidor temporariamente indisponível").
  const { data: session, isLoading: isLoadingSession } = useQuery({
    queryKey: ['auth-session'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error && isAuthDeadError(error)) {
        forceCleanLogout('getSession_error');
        return null;
      }
      return data.session;
    },
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 15 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false, // não retentar — se falhou, forceCleanLogout já redirecionou
  });

  const userId = session?.user?.id ?? null;

  // 🚀 Query única de perfil - usa user.id com fallback por email
  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['user-profile', userId, session?.user?.email],
    queryFn: async () => {
      if (!userId) return null;

      // 1. Primeiro tenta buscar por ID (ideal - IDs sincronizados)
      const { data: usuario, error } = await supabase
        .from('usuarios')
.select('id, nome, email, role, ativo, equipe_id, foto_url, funcao')
        .eq('id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar perfil por ID:', error);
      }

      // 2. Se não encontrou por ID, tenta por EMAIL (fallback)
      let perfil = usuario;
      if (!perfil && session?.user?.email) {
        console.log('🔄 Fallback: buscando perfil por email:', session.user.email);
        const { data: usuarioByEmail, error: emailError } = await supabase
          .from('usuarios')
.select('id, nome, email, role, ativo, equipe_id, foto_url, funcao')
          .eq('email', session.user.email)
          .eq('ativo', true)
          .maybeSingle();

        if (emailError && emailError.code !== 'PGRST116') {
          console.error('Erro ao buscar perfil por email:', emailError);
        }

        perfil = usuarioByEmail;
        
        if (perfil) {
          console.log('✅ Perfil encontrado via fallback por email:', perfil.nome);
        }
      }

      if (!perfil) return null;

      // Buscar roles adicionais em uma única query
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', perfil.id);

      return {
        ...perfil,
        roles: rolesData?.map(r => r.role) || [],
      } as UserProfile;
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 15 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // 🚀 Query única de permissões
  const { data: permissions, isLoading: isLoadingPermissions } = useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('usuario_permissoes')
        .select('*')
        .eq('usuario_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Erro ao buscar permissões:', error);
        return null;
      }

      const rawData = data as unknown as Record<string, unknown>;
      return {
        ver_todos_clientes: rawData?.ver_todos_clientes ?? false,
        ver_todos_pedidos: rawData?.ver_todos_pedidos ?? false
      } as UserPermissions;
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Helpers pré-computados
  const userRole = profile?.role ?? null;
  const isMaster = userRole === 'master';
  const isGerente = userRole === 'gerente' || userRole === 'diretor';
  const isSupervisor = userRole === 'supervisor';
  const isBko = userRole === 'bko';
  const isQualidade = userRole === 'qualidade';
  const isConsultor = userRole === 'consultor';
  const canSeeAll = userRole ? ROLES_ACESSO_TOTAL.includes(userRole) : false;

  const value: UserProfileContextType = useMemo(() => ({
    profile,
    permissions,
    isLoadingProfile: isLoadingSession || isLoadingProfile,
    isLoadingPermissions: isLoadingSession || isLoadingPermissions,
    isAuthenticated: !!session?.user,
    userId,
    userRole,
    isMaster,
    isGerente,
    isSupervisor,
    isBko,
    isQualidade,
    isConsultor,
    canSeeAll,
  }), [
    profile, permissions, isLoadingSession, isLoadingProfile, isLoadingPermissions,
    session?.user, userId, userRole, isMaster, isGerente, isSupervisor,
    isBko, isQualidade, isConsultor, canSeeAll,
  ]);

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfileContext() {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfileContext must be used within a UserProfileProvider');
  }
  return context;
}

// Hook de compatibilidade com useUserProfile existente
export function useOptimizedUserProfile() {
  const ctx = useUserProfileContext();
  return {
    data: ctx.profile,
    isLoading: ctx.isLoadingProfile,
    error: null,
  };
}

// Hook de compatibilidade com useUserPermissions existente  
export function useOptimizedUserPermissions() {
  const ctx = useUserProfileContext();
  return {
    permissions: ctx.permissions,
    isLoading: ctx.isLoadingPermissions,
    error: null,
  };
}
